import { Chip, Stack, SxProps, Theme } from '@mui/material';
import { BakeryDining, EggAlt, Grain, Grass, SetMeal, Spa, WaterDrop } from '@mui/icons-material';
import { AllergenChipItem } from '../../../components/shared/AllergenChips';
import { normalizeRecipeAllergenStatus } from '../../../app/services/api/recipeV2/allergenStatus';
import { AllergenStatus } from '../../../app/services/api/recipeV2';

const allergenIconMap: Record<string, typeof EggAlt> = {
  EGG: EggAlt,
  EGGS: EggAlt,
  GLUTEN: Grain,
  FISH: SetMeal,
  CELERY: Grass,
  MILK: WaterDrop,
  WHEAT: BakeryDining,
  SOY: Spa,
  SOYA: Spa,
  SOYBEANS: Spa,
};

interface RecipeV2AllergenChipsProps {
    allergens: AllergenChipItem[];
    max?: number;
    size?: 'small' | 'medium';
    showName?: boolean;
    spacing?: number;
    sx?: SxProps<Theme>;
}

/** Recipe V2-only status colouring. Shared chip behavior remains unchanged for V1. */
const RecipeV2AllergenChips = ({ allergens, max = 0, size = 'small', showName = false, spacing = 1, sx }: RecipeV2AllergenChipsProps) => {
  const visible = max > 0 ? allergens.slice(0, max) : allergens;
  const overflow = max > 0 && allergens.length > max ? allergens.length - max : 0;
  const iconSize = size === 'small' ? 14 : 18;

  return <Stack
    direction="row"
    spacing={spacing}
    flexWrap="wrap"
    useFlexGap
    sx={sx}
  >
    {visible.map((allergen) => {
      const AllergenIcon = allergenIconMap[allergen.allergenCode.toUpperCase()] ?? Spa;
      const status = normalizeRecipeAllergenStatus(allergen.status);
      const isWarning = status === AllergenStatus.Contains || status === AllergenStatus.MayContain;
      const isVerifiedNegative = status === AllergenStatus.FreeFrom || status === AllergenStatus.NotPresent;
      const color = isWarning ? 'warning.main' : isVerifiedNegative ? 'success.main' : 'grey.500';
      const textColor = isWarning ? 'warning.dark' : isVerifiedNegative ? 'success.dark' : 'text.secondary';
      const label = showName && allergen.allergenName
        ? allergen.allergenName
        : allergen.allergenCode.charAt(0) + allergen.allergenCode.slice(1).toLowerCase();

      return <Chip
        key={allergen.allergenCode}
        icon={<AllergenIcon sx={{ fontSize: iconSize }} />}
        label={label}
        size={size}
        variant="outlined"
        sx={{ px: '1px', borderColor: color, color: textColor, fontWeight: 500, fontSize: size === 'small' ? '0.7rem' : '0.8rem', '& .MuiChip-icon': { color } }}
      />;
    })}
    {overflow > 0 && <Chip
      label={`+${overflow}`}
      size={size}
      variant="outlined"
      sx={{ fontSize: size === 'small' ? '0.7rem' : '0.8rem' }}
    />}
  </Stack>;
};

export default RecipeV2AllergenChips;